/**
 * Error handling utilities for API responses
 */

interface ValidationError {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: any;
  url?: string;
}

interface APIError {
  response?: {
    data?: {
      detail?: string | ValidationError[] | any;
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

/**
 * Format API error for display to user
 * Handles FastAPI validation errors and generic errors
 */
export function formatAPIError(error: any, fallbackMessage = 'An error occurred'): string {
  const apiError = error as APIError;

  // Check if we have a response with data
  if (apiError.response?.data) {
    const { detail, message } = apiError.response.data;

    // If detail is a string, use it
    if (typeof detail === 'string') {
      return detail;
    }

    // If detail is an array of validation errors (FastAPI/Pydantic format)
    if (Array.isArray(detail)) {
      // Extract all error messages and join them
      const errorMessages = detail
        .map((err: ValidationError) => {
          // Format: "field: error message"
          const field = err.loc.slice(1).join('.'); // Skip 'body' prefix
          return field ? `${field}: ${err.msg}` : err.msg;
        })
        .filter(Boolean);

      if (errorMessages.length > 0) {
        return errorMessages.join('; ');
      }
    }

    // If we have a message field
    if (typeof message === 'string') {
      return message;
    }

    // If detail is some other object, try to stringify it
    if (detail && typeof detail === 'object') {
      return JSON.stringify(detail);
    }
  }

  // Fall back to error message
  if (apiError.message) {
    return apiError.message;
  }

  return fallbackMessage;
}

/**
 * Extract field-specific errors from validation errors
 * Returns a map of field names to error messages
 */
export function extractFieldErrors(error: any): Record<string, string> {
  const apiError = error as APIError;
  const fieldErrors: Record<string, string> = {};

  if (apiError.response?.data?.detail && Array.isArray(apiError.response.data.detail)) {
    apiError.response.data.detail.forEach((err: ValidationError) => {
      // Get field name (skip 'body' prefix from FastAPI)
      const fieldPath = err.loc.slice(1);
      if (fieldPath.length > 0) {
        const fieldName = fieldPath.join('.');
        fieldErrors[fieldName] = err.msg;
      }
    });
  }

  return fieldErrors;
}
